import { useEffect, useMemo, useState } from "react"
import { usePage } from "@inertiajs/react"
import { MessageCircleQuestionMark } from "lucide-react"
import { trackConversion } from "@/lib/analytics"
import { stripLocale } from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"
import { AssistantPanel, type AssistantStrings } from "./AssistantPanel"
import { useAssistant } from "./useAssistant"

// The floating "Ask a question" button on every public page of both brands,
// mounted by PublicLayout. Renders nothing unless the admin switch is on
// AND the app can reach Claude. Also the landing side of a cross-brand
// handoff: a ?assistant=<token> in the URL is swapped for the parked
// transcript and the panel opens on it.

const HANDOFF_PARAM = "assistant"

export function AssistantWidget() {
  const page = usePage<PageProps>()
  const { features, assistant_available, current_brand, current_locale, translations } = page.props
  const enabled = Boolean(features.site_assistant) && Boolean(assistant_available)
  const t = translations.assistant ?? {}
  const gisela = current_brand.key === "gisela"

  const strings = useMemo<AssistantStrings>(
    () => ({
      title: t.title ?? "Ask a question",
      intro: (gisela ? t.intro_gisela : t.intro_sanctuary) ?? "",
      placeholder: t.placeholder ?? "",
      send: t.send ?? "Send",
      close: t.close ?? "Close",
      clear: t.clear ?? "Start again",
      thinking: t.thinking ?? "…",
      privacy: t.privacy ?? "",
      suggestions: (gisela ? t.suggestions_gisela : t.suggestions_sanctuary) ?? [],
      crisis: {
        heading: t.crisis?.heading ?? "",
        body: t.crisis?.body ?? "",
        lines: t.crisis?.lines ?? [],
      },
    }),
    [t, gisela],
  )

  const [open, setOpen] = useState(false)
  const pagePath = stripLocale(page.url.split("?")[0], page.props.locale_slugs)
  const assistant = useAssistant({
    page: pagePath,
    locale: current_locale,
    unavailableText: t.unavailable ?? "The assistant isn't available right now.",
  })
  const { loadHandoff } = assistant

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const token = params.get(HANDOFF_PARAM)
    if (!token) return

    params.delete(HANDOFF_PARAM)
    const query = params.toString()
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash,
    )
    void loadHandoff(token).then((loaded) => {
      if (loaded) setOpen(true)
    })
  }, [enabled, loadHandoff])

  if (!enabled) return null

  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) trackConversion("assistant_opened")
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="assistant-open"
          onClick={() => onOpenChange(true)}
          className="btn btn-primary fixed bottom-4 right-4 z-40 gap-2 rounded-full shadow-lg"
        >
          <MessageCircleQuestionMark className="h-5 w-5" aria-hidden="true" />
          <span className="hidden sm:inline">{gisela ? t.open_gisela : t.open_sanctuary}</span>
          <span className="sr-only sm:hidden">{gisela ? t.open_gisela : t.open_sanctuary}</span>
        </button>
      )}
      <AssistantPanel
        open={open}
        onOpenChange={onOpenChange}
        strings={strings}
        messages={assistant.messages}
        status={assistant.status}
        crisis={assistant.crisis}
        onSend={assistant.send}
        onReset={assistant.reset}
        createHandoff={assistant.createHandoff}
      />
    </>
  )
}
